import { ReactElement, ReactNode, useRef, useState, useEffect } from "react";
import { Flex, FlexProps, Heading, HeadingProps } from "@chakra-ui/react";

import { BackToHomeButton } from "../atoms/BackToHomeButton";
import { BackButton } from "../atoms/BackButton";

const BUTTON_SIZE = 40; // ボタンのサイズ md = 40px
const BUTTON_OFFSET = BUTTON_SIZE / 2; // タイトル中央寄せ用のオフセット

export type PageHeaderProps = {
  title: ReactNode;
  titleContainerProps?: FlexProps;
  titleProps?: HeadingProps;
  showBackButton?: boolean;
  showBackToHomeButton?: boolean;
  // 戻るボタンを押したときの処理。省略すると一つ前の画面に戻る
  onBack?: () => void;
};

export function PageHeader(props: Readonly<PageHeaderProps>): ReactElement {
  const {
    title,
    titleContainerProps,
    titleProps,
    showBackButton = false,
    showBackToHomeButton = false,
    onBack,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [isButtonSeparate, setIsButtonSeparate] = useState(false);
  const [isTextLeft, setIsTextLeft] = useState(false);

  useEffect(() => {
    const checkLayout = (): void => {
      if (!containerRef.current || !measureRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const titleWidth = measureRef.current.offsetWidth;

      // タイトル幅 > 親幅 → 左寄せ改行
      const needsLeftAlign = titleWidth > containerWidth;
      // ボタン + タイトル幅 > 親幅 → ボタン別行
      const needsSeparateButton =
        showBackButton && titleWidth + BUTTON_SIZE > containerWidth;

      setIsButtonSeparate(needsSeparateButton);
      setIsTextLeft(needsLeftAlign);
    };

    checkLayout();

    const resizeObserver = new ResizeObserver(checkLayout);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [showBackButton, title]);

  return (
    <Flex
      direction="column"
      width="full"
      ref={containerRef}
      {...titleContainerProps}
    >
      {showBackToHomeButton && <BackToHomeButton />}
      {showBackButton && isButtonSeparate && <BackButton onClick={onBack} />}
      <Flex align="center" width="full">
        {showBackButton && !isButtonSeparate && <BackButton onClick={onBack} />}
        <Heading
          as="h1"
          flex={1}
          fontSize="xl"
          textAlign={isTextLeft ? "left" : "center"}
          lineHeight="64px"
          whiteSpace={isButtonSeparate ? "normal" : "nowrap"}
          ml={showBackButton && !isButtonSeparate ? `-${BUTTON_OFFSET}px` : 0}
          {...titleProps}
        >
          <span
            ref={measureRef}
            style={{
              position: "absolute",
              visibility: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </span>
          {title}
        </Heading>
      </Flex>
    </Flex>
  );
}
